import {
  Box,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  VStack,
} from '@chakra-ui/react';
import * as React from 'react';

import { SendIcon, XIcon } from '@/imports/ui/chakra/feather';
import { useBlueprint } from '@/layouts/components/Blueprint';

import type { AskMessage } from '../../types';
import type { AiAskIndexData } from '../../utils/aiAskHelpers';
import { AIAskHeaderIcon } from './Icons';
import { AIAskMessageRow } from './Messages';

export const AIAskInputBar: React.FC<{
  askQuestion: string;
  askLoading: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  onQuestionChange: (value: string) => void;
  onSubmit: () => void;
  onNewChat: () => void;
  onClose: () => void;
}> = ({
  askQuestion,
  askLoading,
  inputRef,
  onQuestionChange,
  onSubmit,
  onNewChat,
  onClose,
}) => (
  <Box borderTopWidth="1px" bg="#F7FAFC" py="4" px="6">
    <VStack w="100%" spacing={2} align="stretch">
      <InputGroup w="100%">
        <Input
          ref={inputRef}
          placeholder="Type a command..."
          bg="#E2E8F0"
          borderColor="#E2E8F0"
          borderRadius="12px"
          pr="10"
          _placeholder={{ color: 'gray.500' }}
          value={askQuestion}
          onChange={(e) => onQuestionChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              onClose();
              return;
            }
            if (
              e.key === 'Enter' &&
              !e.shiftKey &&
              !(e.nativeEvent as KeyboardEvent).isComposing
            ) {
              e.preventDefault();
              onSubmit();
            }
          }}
          isDisabled={askLoading}
        />
        <InputRightElement h="full" w="10">
          <Box
            as="button"
            type="button"
            display="flex"
            alignItems="center"
            justifyContent="center"
            h="full"
            w="full"
            minW="10"
            bg="transparent"
            border="none"
            cursor={
              askLoading || !askQuestion.trim() ? 'not-allowed' : 'pointer'
            }
            opacity={askLoading || !askQuestion.trim() ? 0.5 : 1}
            aria-label="Send"
            onClick={() => {
              if (!askLoading && askQuestion.trim()) onSubmit();
            }}
          >
            <SendIcon color="#1E78D7" boxSize="5" />
          </Box>
        </InputRightElement>
      </InputGroup>

      <Box
        as="button"
        type="button"
        w="100%"
        h="42px"
        bg="#E2E8F0"
        border="1px solid"
        borderColor="#E2E8F0"
        borderRadius="12px"
        color="#4A5568"
        fontSize="sm"
        fontWeight="600"
        cursor={askLoading ? 'not-allowed' : 'pointer'}
        opacity={askLoading ? 0.6 : 1}
        onClick={onNewChat}
        disabled={askLoading}
        _hover={{ bg: '#D8E0EA' }}
      >
        New chat
      </Box>
    </VStack>
  </Box>
);

export const AIAskDrawer: React.FC<{
  isOpen: boolean;
  askError: string;
  askMessages: AskMessage[];
  askQuestion: string;
  askLoading: boolean;
  indexData: AiAskIndexData;
  appOrigin: string;
  viewResultsLoadingIndex: number | null;
  inputRef: React.RefObject<HTMLInputElement>;
  onClose: () => void;
  onQuestionChange: (value: string) => void;
  onSubmit: () => void;
  onNewChat: () => void;
  onViewResults: (messageIndex: number) => void;
}> = ({
  isOpen,
  askError,
  askMessages,
  askQuestion,
  askLoading,
  indexData,
  appOrigin,
  viewResultsLoadingIndex,
  inputRef,
  onClose,
  onQuestionChange,
  onSubmit,
  onNewChat,
  onViewResults,
}) => {
  const { headerHeight } = useBlueprint();
  const messagesScrollRef = React.useRef<HTMLDivElement>(null);
  const shouldSmoothScrollRef = React.useRef(false);

  React.useEffect(() => {
    if (!isOpen) {
      shouldSmoothScrollRef.current = false;
      return;
    }

    const scrollContainer = messagesScrollRef.current;
    if (!scrollContainer) return;

    const useSmoothScroll = shouldSmoothScrollRef.current;
    shouldSmoothScrollRef.current = true;

    const scrollToBottom = (behavior: ScrollBehavior) => {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior,
      });
    };

    if (useSmoothScroll) {
      scrollToBottom('smooth');
      return;
    }

    requestAnimationFrame(() => {
      scrollToBottom('auto');
      requestAnimationFrame(() => scrollToBottom('auto'));
    });
  }, [isOpen, askMessages, askLoading]);

  if (!isOpen) return null;

  const mobileBottomOffset =
    'calc(5rem + env(safe-area-inset-bottom, 0px))';

  return (
    <Box
      position="fixed"
      top={`${headerHeight}px`}
      bottom={{ base: mobileBottomOffset, lg: 0 }}
      right={0}
      w={{ base: '100vw', md: '450px', lg: '450px' }}
      borderLeftWidth="1px"
      display="flex"
      flexDirection="column"
      zIndex={1300}
      bg="white"
      boxShadow="lg"
    >
      <IconButton
        icon={<XIcon boxSize="5" />}
        aria-label="Close AI Ask"
        position="absolute"
        top="3"
        right="4"
        size="sm"
        color="gray.500"
        bg="transparent"
        onClick={onClose}
        _hover={{ bg: 'transparent', color: 'gray.700' }}
        _active={{ bg: 'transparent' }}
      />

      <Box borderBottomWidth="1px" bg="#1676CE0D" py="3" pr="14" px="6">
        <HStack spacing="3" alignItems="center">
          <AIAskHeaderIcon />
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            minH="6"
          >
            <Text
              fontSize="lg"
              lineHeight="1.5"
              fontWeight="600"
              color="gray.900"
            >
              Tradewing IQ
            </Text>
            <Text
              fontSize="sm"
              lineHeight="1.5"
              fontWeight="400"
              color="gray.900"
            >
              Powered by Community Intelligence
            </Text>
          </Box>
        </HStack>
      </Box>

      <Box
        bg="#F7FAFC"
        px="5"
        pt="2"
        pb="4"
        display="flex"
        flexDirection="column"
        minH={0}
        flex="1"
      >
        {askError ? (
          <Text fontSize="sm" color="red.500" mb={3}>
            {askError}
          </Text>
        ) : null}

        <VStack
          ref={messagesScrollRef}
          align="stretch"
          spacing={4}
          className="ai-ask-messages"
          flex="1"
          minH={0}
          overflowY="auto"
          pr={1}
        >
          {askMessages.map((message, index) => (
            <AIAskMessageRow
              key={index}
              message={message}
              indexData={indexData}
              appOrigin={appOrigin}
              onViewResults={() => onViewResults(index)}
              isViewResultsLoading={viewResultsLoadingIndex === index}
            />
          ))}
        </VStack>
      </Box>

      <AIAskInputBar
        askQuestion={askQuestion}
        askLoading={askLoading}
        inputRef={inputRef}
        onQuestionChange={onQuestionChange}
        onSubmit={onSubmit}
        onNewChat={onNewChat}
        onClose={onClose}
      />
    </Box>
  );
};
